import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'

// Import favicons
import faviconSvg from '../assets/favicon/favicon.svg'
import faviconIco from '../assets/favicon/favicon.ico'
import favicon96 from '../assets/favicon/favicon-96x96.png'
import appleTouchIcon from '../assets/favicon/apple-touch-icon.png'
import webManifest from '../assets/favicon/site.webmanifest'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolledPastHero, setIsScrolledPastHero] = useState(false)
  const location = useLocation()

  // Set favicons and icons
  useEffect(() => {
    const setLinkTag = (rel: string, href: string, type?: string, sizes?: string) => {
      let link = document.querySelector(`link[rel='${rel}']${sizes ? `[sizes='${sizes}']` : ''}`) as HTMLLinkElement
      if (!link) {
        link = document.createElement('link')
        link.rel = rel
        document.head.appendChild(link)
      }
      link.href = href
      if (type) link.type = type
      if (sizes) link.setAttribute('sizes', sizes)
    }

    // Standard favicon
    setLinkTag('icon', faviconIco)

    // SVG favicon (modern browsers)
    setLinkTag('icon', faviconSvg, 'image/svg+xml')

    // PNG favicon
    setLinkTag('icon', favicon96, 'image/png', '96x96')

    // Apple touch icon
    setLinkTag('apple-touch-icon', appleTouchIcon, undefined, '180x180')

    // Web manifest
    setLinkTag('manifest', webManifest)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Calculate when user has scrolled past the hero section
      // Hero starts at 100vh and shrinks, so we check for ~50vh scroll point
      const heroHeight = Math.max(50, 100 - currentScrollY * 0.1)
      const viewportHeight = window.innerHeight
      const heroPixelHeight = (heroHeight / 100) * viewportHeight

      setIsScrolledPastHero(currentScrollY > heroPixelHeight * 0.8)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Solar Panels', path: '/solar-panels' },
    { name: 'Heat Pumps', path: '/heat-pumps' }
  ]

  const isActive = (path: string) => location.pathname === path

  // Dynamic header styling based on scroll position
  const headerClasses = isScrolledPastHero
    ? 'bg-white shadow-lg text-dark-plum'
    : 'bg-dark-background bg-opacity-90 backdrop-blur-sm text-white shadow-sm'

  const logoVariant = isScrolledPastHero ? 'dark' : 'light'
  const navTextClass = isScrolledPastHero ? 'text-dark-plum' : 'text-white'
  const mobileButtonClass = isScrolledPastHero ? 'text-dark-plum' : 'text-white'

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerClasses}`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center">
            <Logo variant={logoVariant} />
           </Link>

          <nav className="hidden lg:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-primary-green'
                    : `${navTextClass} hover:text-primary-green`
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/quote"
              className="btn-primary hover:bg-primary-green-dark hover:text-white"
              aria-label="Get a free quote"
            >
              Get a FREE quote
            </Link>
          </div>

          <button
            className={`lg:hidden p-2 transition-colors duration-300 ${mobileButtonClass}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={isMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden pb-4">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-primary-green'
                      : `${navTextClass} hover:text-primary-green`
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="btn-primary inline-block text-center mt-4"
                onClick={() => setIsMenuOpen(false)}
              >
                Get Free Quote
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header